/**
 * Getting a photograph ready to be read, in the browser.
 *
 * Three jobs: honour the rotation the camera recorded, bring the size down to
 * something worth sending, and encode it as base64 for the mutation that
 * carries it. Compression happens here rather than on the server because the
 * point of it is the upload — a 12-megapixel photograph is four megabytes on a
 * phone connection, and the server would only be throwing most of it away.
 *
 * No new dependency. `createImageBitmap` and `OffscreenCanvas` do resizing,
 * re-encoding and EXIF orientation between them, which is the whole job; a
 * compression library would be a second image pipeline for something the
 * platform already does.
 */

/**
 * The longest edge a page is sent at.
 *
 * Not an arbitrary small number. A page filling 1600px carries body text at
 * roughly 8–10 pixels per character, which is comfortably above what a reader
 * needs for print — and a page reduced to, say, 800px loses subscripts,
 * exponents and the difference between a comma and a full stop, which is
 * exactly the content a maths worksheet is made of. Going higher buys detail
 * the reader does not use and costs the tutor's upload.
 */
export const MAX_EDGE = 1600

/**
 * JPEG quality.
 *
 * High enough that text edges stay crisp and a diagram is still legible; low
 * enough that a page lands in the low hundreds of kilobytes rather than
 * megabytes. Below about 0.7, JPEG ringing starts to appear around glyphs,
 * which is the one artefact that costs a reader accuracy.
 */
export const QUALITY = 0.82

/** JPEG, because a photograph of a page is a photograph. */
export const OUTPUT_MIME = 'image/jpeg' as const

/** A hard ceiling on what will be decoded at all. Beyond this it is not a page. */
export const MAX_PIXELS = 80_000_000

export interface CompressedImage {
  /** Base64 with no `data:` prefix, which is what the mutation takes. */
  base64: string
  mimeType: typeof OUTPUT_MIME
  width: number
  height: number
  /** The encoded size, for the log and for the tests. */
  bytes: number
}

export class ImageTooLargeError extends Error {
  constructor(pixels: number) {
    super(`image has ${pixels} pixels`)
    this.name = 'ImageTooLargeError'
  }
}

/** The size that fits inside `maxEdge` without changing the proportions. */
export function fitWithin(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number; scale: number } {
  const longest = Math.max(width, height)
  if (longest <= maxEdge) return { width, height, scale: 1 }
  const scale = maxEdge / longest
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    scale,
  }
}

/** Bytes to base64, in chunks so a megabyte does not blow the argument list. */
export function toBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000
  let binary = ''
  for (let index = 0; index < bytes.length; index += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(index, index + CHUNK))
  }
  return btoa(binary)
}

/**
 * One chosen file, compressed and encoded.
 *
 * `imageOrientation: 'from-image'` is what handles EXIF: the browser applies
 * the camera's rotation while decoding, so a portrait photograph taken on a
 * phone arrives portrait. Without it the pixels come out as the sensor read
 * them, and a reader handed a page on its side returns very little — the
 * orientation is not cosmetic here, it decides whether the text is legible at
 * all.
 *
 * A page that already fits is still re-encoded, so what goes up is a known
 * format and quality rather than whatever the camera chose. The bitmap is
 * closed either way, so nothing holds the decoded surface.
 */
export async function compressForExtraction(
  file: Blob,
  maxEdge: number = MAX_EDGE,
): Promise<CompressedImage> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })

  try {
    const pixels = bitmap.width * bitmap.height
    if (pixels > MAX_PIXELS) throw new ImageTooLargeError(pixels)

    const target = fitWithin(bitmap.width, bitmap.height, maxEdge)
    const canvas = new OffscreenCanvas(target.width, target.height)
    const context = canvas.getContext('2d')
    if (!context) throw new Error('no 2d context')

    context.drawImage(bitmap, 0, 0, target.width, target.height)
    const blob = await canvas.convertToBlob({ type: OUTPUT_MIME, quality: QUALITY })
    const bytes = new Uint8Array(await blob.arrayBuffer())

    return {
      base64: toBase64(bytes),
      mimeType: OUTPUT_MIME,
      width: target.width,
      height: target.height,
      bytes: bytes.length,
    }
  } finally {
    bitmap.close()
  }
}
